export interface TableColumn {
  id?: string;
  [key: string]: any;
}

export type RowType = Record<string, unknown>;

export interface RowWithClientId {
  client_id?: number | null;
}

export interface BudgetRowType extends RowWithClientId {
  sah_budget_id?: string;
  origin_tag?: string;
}

// TAV-3233 issue 3: the ids of every column this row is not allowed to edit, per the column's own
// row-level `isEditable` predicate.
//
// Locking a cell only stops a keystroke reaching the server. The row still carries the value it was
// loaded with, so an edit to any OTHER cell would PUT that stale copy back and overwrite whatever
// has been written since. Every SA-owned budget column is `allowUpdate: true` on the API model
// (api/src/models/tav/client_sah_budgets.mjs), so the write lands. Open the modal, let Services
// Australia refresh in the background, tick Is Archived, and the pre-refresh totals go back up.
//
// Derived from the columns rather than a hardcoded field list so a change to a lock cannot drift
// away from the payload it is supposed to govern.
//
// Deliberately only the FUNCTION form. A plain `isEditable: false` marks a column that is read-only
// for every row (available_amount, prior_spent_included_in_statements), and those are already
// handled at the call sites and by the server; narrowing to the predicate form keeps this to the
// per-row locks and leaves the create path (whose rows are always 'manual') untouched.
function lockedFieldIds(row: unknown, columns: TableColumn[]): Set<string> {
  return new Set(
    columns
      .filter((col) => typeof col.isEditable === 'function' && !col.isEditable(row))
      .map((col) => col.id)
      .filter((id): id is string => id !== undefined),
  );
}

export function filterBudgetRowFields<T extends BudgetRowType>(
  row: T,
  columns: TableColumn[],
  clientId: number | null,
  sahBudgetId?: string,
): Partial<T> & { sah_budget_id?: string; origin_tag: string; client_id?: number | null } {
  const locked = lockedFieldIds(row, columns);
  const filtered = filterRowFields(row, columns, clientId);
  const withoutLocked = Object.fromEntries(Object.entries(filtered).filter(([key]) => !locked.has(key)));

  return {
    ...withoutLocked,
    // Restored only while the column is still writable for this row. `filterRowFields` drops
    // nothing that is locked, so without the guard an api row's stale budget id would be put back
    // here after being filtered out above. A new row is always 'manual', so the create path (the
    // only caller that passes sahBudgetId) is unaffected.
    ...(locked.has('sah_budget_id') ? {} : { sah_budget_id: sahBudgetId ?? row.sah_budget_id }),
    origin_tag: row.origin_tag || 'manual',
  } as Partial<T> & { sah_budget_id?: string; origin_tag: string; client_id?: number | null };
}

// Same idea as filterBudgetRowFields for services (TAV-3420): strip every field this row's own
// `isEditable` predicates lock, so editing an unlocked cell cannot write a stale copy of an imported
// value back. `origin_tag` is never sent: the server defaults it to 'manual' on create and leaves it
// alone on update.
export function filterServiceRowFields<T extends RowWithClientId>(
  row: T,
  columns: TableColumn[],
  clientId: number | null,
): Partial<T> & { client_id?: number | null } {
  const locked = lockedFieldIds(row, columns);
  const filtered = filterRowFields(row, columns, clientId);
  return Object.fromEntries(Object.entries(filtered).filter(([key]) => !locked.has(key))) as Partial<T> & {
    client_id?: number | null;
  };
}

export function filterRowFields<T extends RowWithClientId>(
  row: T,
  columns: TableColumn[],
  clientId: number | null,
): T & { client_id: number | null } {
  // Dotted column ids are display-only derived columns (e.g. category.max_contrib_proportion)
  // — table-view writes their derived values onto rows as literal dotted keys, which must
  // not leak into create/update payloads.
  const allowedFields = columns
    .map((col) => col.id)
    .filter((id): id is string => id !== undefined && !id.includes('.'));

  const rowWithClientId = {
    ...row,
    client_id: row.client_id ?? clientId,
  };

  const filtered = Object.fromEntries(
    Object.entries(rowWithClientId).filter(
      ([key, value]) => allowedFields.includes(key) || (key === 'client_id' && value !== null),
    ),
  );

  return filtered as T & { client_id: number | null };
}
